import qs from 'qs';
import { AxiosResponse } from 'axios';
import api from '../../utils/api';
import { Documents, DocumentsType } from '~/types/documents';
import { Pagination, SelectItem } from '~/types';
import { PAGE_SIZE } from '~/constants';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export async function getDocumentsTypeAsSelect(): Promise<SelectItem[]> {
  const { data } = await api.get<DocumentsType[]>('/documento_tipo/ativos');

  const types = data.map(type => ({
    value: String(type.id_tipo),
    label: type.nome,
  }));

  return types;
}

export async function getDocumentsTypes(): Promise<DocumentsType[]> {
  const { data } = await api.get<DocumentsType[]>('/documento_tipo');
  return data;
}

export async function getAllDocumentsTypes(): Promise<
  Pagination<DocumentsType>
> {
  const { data } = await api.get<Pagination<DocumentsType>>('/documento_tipo');
  return data;
}

export async function getDocumentsList(
  page: number,
  orderby: string,
  order: string,
  search: string,
  ids_tipos: number[],
  query?: any,
  size = PAGE_SIZE,
): Promise<Pagination<Documents>> {
  const { data } = await api.get<Pagination<Documents>>('/documento', {
    params: {
      page,
      orderby,
      order,
      size,
      ...(search ? { search } : {}),
      ...{ ...query },
      ...(ids_tipos.length > 0 && { ids_tipos }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });
  return data;
}

export async function getDocumentsByContractId(
  id: number,
): Promise<Documents[]> {
  const { data } = await api.get<Documents[]>(
    `/documento_contrato/${id}/list_documentos`,
  );

  const documentData = data.map(document => ({
    ...document,
    data_cadastro: formatInTimeZone(document.data_cadastro, 'dd/MM/yyyy'),
  }));

  return documentData;
}

export async function deleteDocument(id: number): Promise<AxiosResponse<any>> {
  return api.delete(`/documento/${id}`);
}

export async function downloadDocument(
  id: number,
): Promise<AxiosResponse<any>> {
  return api.get(`/documento/${id}/download`, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getDocumentsXlsxCsv(
  search: string,
  orderby: string,
  order: string,
  ids_tipos: number[],
  tipo_arquivo: number,
  query?: QueryFilterContext,
): Promise<AxiosResponse<any>> {
  const url = `/documento/${tipo_arquivo === 1 ? 'csv' : 'xlsx'}`;
  const groups = query?.groups?.map(e => e.value);
  const headoffices = query?.headOffices?.map(e => e.value);
  const branches = query?.branches?.map(e => e.value);
  return api.get(url, {
    params: {
      ...(search ? { search } : {}),
      ...(orderby && { orderby }),
      ...(order && { order }),
      ...(groups && { groups }),
      ...(headoffices && { headoffices }),
      ...(branches && { branches }),
      ...(ids_tipos.length > 0 && { ids_tipos }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': `${
        tipo_arquivo === 0 ? 'application/csv' : 'application/xlsx'
      }`,
    },
    responseType: 'blob' as 'json',
  });
}

export async function downloadSelectedDocuments(
  ids: number[],
  query?: QueryFilterContext,
  downloadAll = false,
): Promise<AxiosResponse<any>> {
  const url = '/documento/download_arquivos';
  const groups = query?.groups?.map(e => e.value);
  const headoffices = query?.headOffices?.map(e => e.value);
  const branches = query?.branches?.map(e => e.value);
  return api.get(url, {
    params: {
      ...(groups && { groups }),
      ...(headoffices && { headoffices }),
      ...(branches && { branches }),
      baixarTodos: downloadAll,
      documentos: JSON.stringify(ids),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });
}

export async function downloadBackgroundDocuments(
  docUUID: string,
  query?: QueryFilterContext,
): Promise<AxiosResponse<any>> {
  const url = '/documento/retorna_zip';
  return api.get(url, {
    params: {
      doc_uuid: docUUID,
    },
    headers: {
      'Content-Type': 'application/zip',
    },
    responseType: 'blob' as 'json',
  });
}
